import { EstateInsightsBundleSettingsModalFormShape } from '@/modules/presentation/clientPresentation/components/settingsModals/EstateInsightsBundleSettingsModal/EstateInsightsBundleSettingsModalFormShape';
import {
  EstateInsightStatus,
  PresentationBundleKind,
  PresentationPageKind,
} from '@/types/schema';

import {
  getNewBundleId,
  getNewPageId,
} from '../../../ClientPresentationDesigner.utils';
import { ClientPresentationDesignerV2_EstateInsightFragment } from '../../../graphql/ClientPresentationDesignerV2.generated';
import {
  ClientPresentationV2Bundle,
  ClientPresentationV2EstateInsightsBundleConfiguration,
} from '../../../types/ClientPresentation.PresentationBundleType';
import { ClientPresentationV2Page } from '../../../types/ClientPresentation.PresentationPageType';

export const DEFAULT_ESTATE_INSIGHTS_BUNDLE_VALUES: EstateInsightsBundleSettingsModalFormShape =
  {
    includeMissingDocumentsPage: true,
    estateInsightSelections: {},
  };

export const EMPTY_ESTATE_INSIGHTS_BUNDLE_CONFIGURATION: ClientPresentationV2EstateInsightsBundleConfiguration =
  {
    includeMissingDocumentsPage: false,
    estateInsights: [],
  };

/**
 * Creates default values for the estate insights form
 */
export function createDefaultValues(
  bundle: ClientPresentationV2Bundle | undefined,
  validInsights: Array<{ id: string }>,
  missingDocumentCount: number,
  updateType?: 'create' | 'update' | 'duplicate' | null
): EstateInsightsBundleSettingsModalFormShape {
  // Create default values for the form
  let defaultValues: EstateInsightsBundleSettingsModalFormShape = {
    ...DEFAULT_ESTATE_INSIGHTS_BUNDLE_VALUES,
    estateInsightSelections: {},
  };

  // If updating existing bundle, use its configuration
  if (updateType === 'update' && bundle?.estateInsightsBundleConfiguration) {
    // Get the existing configuration
    const existingConfig = bundle.estateInsightsBundleConfiguration;

    const { estateInsights } = existingConfig;
    const savedInsightIds = new Set(
      estateInsights.map((insight) => insight.id)
    );
    defaultValues = {
      includeMissingDocumentsPage: existingConfig.includeMissingDocumentsPage,
      estateInsightSelections: Object.fromEntries(
        validInsights.map((insight) => [
          insight.id,
          savedInsightIds.has(insight.id),
        ])
      ),
    };
  } else {
    // For new bundles, select existing household insights by default
    validInsights.forEach((insight) => {
      defaultValues.estateInsightSelections[insight.id] = true;
    });
    defaultValues.includeMissingDocumentsPage = missingDocumentCount > 0;
  }

  return defaultValues;
}

/**
 * Creates form field maps for active and completed insights
 */
export function createFieldMaps(
  insights: Array<{ id: string; status: EstateInsightStatus }>
) {
  const activeFieldMap: Record<string, string> = {};
  const completedFieldMap: Record<string, string> = {};

  insights.forEach((insight) => {
    const fieldName = `estateInsightSelections.${insight.id}`;
    if (insight.status === EstateInsightStatus.Active) {
      activeFieldMap[insight.id] = fieldName;
    } else if (insight.status === EstateInsightStatus.Completed) {
      completedFieldMap[insight.id] = fieldName;
    }
  });

  return { activeFieldMap, completedFieldMap };
}

/**
 * The page of this kind the bundle already has, or a new one.
 *
 * This bundle holds at most one page of each kind, so the kind identifies it.
 * Minting a fresh id on every edit asked the save to delete the server's three
 * pages and create three more, which threw away whatever was on them and gave
 * the user new slides in place of the ones they were editing.
 */
function reusePage(
  existingPages: ClientPresentationV2Page[],
  type: PresentationPageKind
): ClientPresentationV2Page {
  const existing = existingPages.find((page) => page.type === type);
  return existing ? { ...existing } : { id: getNewPageId(type), type };
}

/**
 * Processes form data and creates/updates a bundle
 */
export function processFormDataAndCreateBundle(
  formData: EstateInsightsBundleSettingsModalFormShape,
  bundle: ClientPresentationV2Bundle | undefined,
  householdEstateInsights: ClientPresentationDesignerV2_EstateInsightFragment[],
  updateType?: 'create' | 'update' | 'duplicate' | null
): ClientPresentationV2Bundle {
  // Determine if this is an update or create
  const isUpdate = updateType === 'update' && !!bundle;

  // Create a new bundle or clone the existing one
  const newBundle: ClientPresentationV2Bundle = isUpdate
    ? { ...bundle }
    : {
        id: getNewBundleId(PresentationBundleKind.EstateInsightsBundle),
        type: PresentationBundleKind.EstateInsightsBundle,
        displayName: 'Insights',
        pages: [],
      };

  const config: ClientPresentationV2EstateInsightsBundleConfiguration = {
    includeMissingDocumentsPage: formData.includeMissingDocumentsPage || false,
    estateInsights: householdEstateInsights.filter(
      (insight) => formData.estateInsightSelections[insight.id]
    ),
  };

  // Only an update may reuse a page. A duplicate is handed the *source* bundle,
  // and adopting its page ids would make the copy's save write into the pages of
  // the bundle it was copied from.
  const reusablePages = isUpdate ? bundle.pages : [];

  const pages: ClientPresentationV2Page[] = [];
  if (config.includeMissingDocumentsPage) {
    pages.push(
      reusePage(reusablePages, PresentationPageKind.MissingDocumentsPage)
    );
  }
  // Always add both ActiveEstateInsightsPage and CompletedEstateInsightsPage by default
  pages.push(
    reusePage(reusablePages, PresentationPageKind.ActiveEstateInsightsPage)
  );
  pages.push(
    reusePage(reusablePages, PresentationPageKind.CompletedEstateInsightsPage)
  );

  // Set the configuration & pages
  newBundle.estateInsightsBundleConfiguration = config;
  newBundle.pages = pages;

  return newBundle;
}
